/**
 * analyser 읽기 API 클라이언트.
 *
 * 원래 features/upbit/upbitApi.ts 였다. 업비트 전략 라인이 전부 종료되고 살아남은
 * 대시보드가 Binance alert-short와 IBKR(swing/TFSA)뿐이라, 죽은 feature 디렉터리에
 * 산 코드가 얹혀 있는 상태를 정리하면서 여기로 옮겼다.
 *
 * analyser가 도메인별로 쪼개지므로(mercury-analyser-binance / -ibkr) base도
 * 타깃별로 받는다. dev는 setupProxy.js, prod는 CloudFront behavior가 라우팅한다.
 */

export type AnalyserTarget = 'binance' | 'ibkr';

/**
 * 2026-07-25: analyser 가 실제로 둘로 갈렸다 (mercury-analyser VM 의 :8088/:8089).
 * CloudFront behavior 도 /mercury-ibkr/* 가 추가돼 각각 다른 origin 을 탄다.
 *
 * 2026-09-17: CloudFront/S3 를 걷어내고 OCI(mercury-dashboard, nginx)로 옮기면서
 * binance 쪽 경로도 낡은 `/mercury-upbit` 대신 `/mercury-analyser-binance` 로
 * 맞췄다 — origin 은 원래도 mercury-analyser-binance(도쿄) 였다, 이름만 CloudFront
 * behavior 에 묶여 있었을 뿐.
 */
const DEFAULT_BASE: Record<AnalyserTarget, string> = {
  binance: process.env.REACT_APP_BINANCE_ANALYSER_BASE || '/mercury-analyser-binance',
  ibkr: process.env.REACT_APP_IBKR_ANALYSER_BASE || '/mercury-ibkr',
};

export function analyserApiUrl(target: AnalyserTarget, path: string): string {
  const base = DEFAULT_BASE[target].replace(/\/$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

export function isFetchAbortedError(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  if (e.name === 'AbortError') return true;
  return /aborted|AbortError|signal is aborted/i.test(e.message);
}

export async function analyserFetchJSON<T>(
  target: AnalyserTarget,
  path: string,
  options?: {
    timeoutMs?: number;
    signal?: AbortSignal;
    /** 서버가 실은 페이로드 나이(초). analyser 가 캐시로 답하면 0 이 아니다 --
     *  값이 낡았는지 화면이 알아야 "멈춘 화면"을 최신인 척 그리지 않는다
     *  (2026-08-19: 캐시를 넣고 이걸 안 붙여서 유저가 옛 값을 보고 있었다). */
    onAgeS?: (ageS: number | null) => void;
  },
): Promise<T> {
  const url = analyserApiUrl(target, path);
  const timeoutMs = options?.timeoutMs ?? 15000;
  const timeoutCtrl = new AbortController();
  const timer = window.setTimeout(() => timeoutCtrl.abort(), timeoutMs);

  const onExternalAbort = () => timeoutCtrl.abort();
  if (options?.signal) {
    if (options.signal.aborted) {
      window.clearTimeout(timer);
      throw new DOMException('Aborted', 'AbortError');
    }
    options.signal.addEventListener('abort', onExternalAbort, { once: true });
  }

  let res: Response;
  try {
    res = await fetch(url, { signal: timeoutCtrl.signal });
  } catch (e) {
    if (isFetchAbortedError(e)) {
      if (options?.signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      throw new Error(`요청 시간 초과 (${Math.round(timeoutMs / 1000)}s) — analyser API 응답 없음`);
    }
    throw new Error(`네트워크 오류 — analyser API(${url}) 연결 실패`);
  } finally {
    window.clearTimeout(timer);
    if (options?.signal) {
      options.signal.removeEventListener('abort', onExternalAbort);
    }
  }

  if (options?.onAgeS) {
    const raw = res.headers.get('X-Payload-Age-S');
    const n = raw == null ? NaN : Number(raw);
    options.onAgeS(Number.isFinite(n) ? n : null);
  }
  const text = await res.text();
  if (!res.ok) {
    const hint = text.trim().startsWith('<') ? `HTML 응답 — ${DEFAULT_BASE[target]} 프록시 확인` : text.slice(0, 160);
    throw new Error(`HTTP ${res.status}: ${hint}`);
  }
  if (!text.trim()) {
    return [] as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    const trimmed = text.trim();
    if (trimmed.startsWith('<') || trimmed.includes('<!DOCTYPE')) {
      throw new Error(`JSON 파싱 실패 (${url}) — HTML 응답, ${DEFAULT_BASE[target]} 프록시 확인`);
    }
    throw new Error(`JSON 파싱 실패 (${url}): ${trimmed.slice(0, 120)}`);
  }
}

/**
 * 쓰기(POST). 읽기와 달리 **에러 본문의 JSON 을 그대로 살려 던진다** — 서버가
 * `{"error": "..."}` 로 사유를 주고, 화면은 그 사유를 사람에게 보여야 하기 때문이다.
 *
 * 2026-08-17 수동 청산 버튼 때문에 추가됐다. 이 파일에서 유일하게 상태를 바꾸는
 * 경로이므로, 새 호출자를 붙이기 전에 그게 정말 GET 으로 안 되는 일인지 먼저 보라.
 */
export async function analyserPostJSON<T>(
  target: AnalyserTarget,
  path: string,
  body: unknown,
  options?: { timeoutMs?: number; headers?: Record<string, string> },
): Promise<T> {
  const url = analyserApiUrl(target, path);
  const timeoutMs = options?.timeoutMs ?? 30000;
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), timeoutMs);

  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(options?.headers ?? {}) },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: ctrl.signal,
    });
  } catch (e) {
    if (isFetchAbortedError(e)) {
      throw new Error(`요청 시간 초과 (${Math.round(timeoutMs / 1000)}s) — analyser API 응답 없음`);
    }
    // **원인을 삼키지 않는다.** 그냥 "네트워크 오류" 만 던지면 확장프로그램 차단·
    // CORS·DNS 가 전부 같은 문장이 되어 화면만 보고는 진단이 불가능하다
    // (2026-08-17 패스키 등록이 정확히 이 벽에 막혔다 — 서버는 curl 로 200 인데
    // 브라우저만 실패했고, 메시지에 아무 단서가 없었다).
    const cause = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
    throw new Error(`네트워크 오류 — analyser API(${url}) 연결 실패 [${cause}]`);
  } finally {
    window.clearTimeout(timer);
  }

  const text = await res.text();
  if (!res.ok) {
    let msg = text.slice(0, 200);
    try {
      const j = JSON.parse(text) as { error?: string };
      if (j.error) msg = j.error;
    } catch { /* 본문이 JSON 이 아니면 그대로 쓴다 */ }
    throw new Error(`HTTP ${res.status}: ${msg}`);
  }
  return (text.trim() ? JSON.parse(text) : {}) as T;
}
